import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../helper/apiconstant';
import { buildPaginationItems, buildPaginationLabel, buildQueryString, canShowPagination, extractPagination } from '../../helper/table-utils';
import { DEFAULT_DASHBOARD_USER } from '../dashboard/dashboard.data';
import { Httpservice } from '../../services/httpservice';
import { NotificationRefreshService } from '../../services/notification-refresh';
import { AppPreferencesService } from '../../services/app-preferences';
import { ReportShellComponent } from '../structure/report-shell/report-shell';

interface UserNotification {
  id: number;
  title: string;
  description: string | null;
  redirect_url: string | null;
  is_read: boolean;
  created_at: string | null;
  created_at_ad: string | null;
  created_at_bs: string | null;
}

interface NotificationPagination {
  page_no: number;
  page_size: number;
  total_records: number;
  total_pages: number;
}

@Component({
  selector: 'app-notifications-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ReportShellComponent],
  templateUrl: './notifications.html',
  styleUrl: './notifications.scss',
})
export class NotificationsPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isMarkingAllRead = false;
  protected unreadCount = 0;
  protected notifications: UserNotification[] = [];
  protected readonly pageSizeOptions = [15, 25, 50, 100];
  protected readonly pageSizeControl = new FormControl(15, { nonNullable: true });
  protected pagination: NotificationPagination = {
    page_no: 1,
    page_size: 15,
    total_records: 0,
    total_pages: 0,
  };

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private notificationRequestId = 0;

  constructor(
    private readonly httpSvc: Httpservice,
    private readonly router: Router,
    private readonly notificationRefreshSvc: NotificationRefreshService,
    private readonly preferencesSvc: AppPreferencesService,
  ) {}

  ngOnInit(): void {
    this.loadNotifications(1);
    this.pageSizeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadNotifications(1));
  }

  protected handleMenuToggle(): void {
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected markAllRead(): void {
    if (!this.unreadCount || this.isMarkingAllRead) {
      return;
    }

    this.isMarkingAllRead = true;
    this.httpSvc
      .requestCall(AuthEndPoints.MARK_ALL_USER_NOTIFICATIONS_READ, ApiMethod.PATCH, {}, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notifications = this.notifications.map((notification) => ({ ...notification, is_read: true }));
          this.unreadCount = 0;
          this.isMarkingAllRead = false;
          this.notificationRefreshSvc.notify();
          this.cdr.markForCheck();
        },
        error: () => {
          this.isMarkingAllRead = false;
          this.cdr.markForCheck();
        },
      });
  }

  protected openNotification(notification: UserNotification): void {
    const openRedirect = (): void => {
      if (!notification.redirect_url) {
        return;
      }

      if (/^https?:\/\//i.test(notification.redirect_url)) {
        window.location.assign(notification.redirect_url);
        return;
      }

      this.router.navigateByUrl(notification.redirect_url);
    };

    if (notification.is_read) {
      openRedirect();
      return;
    }

    this.httpSvc
      .requestCall(`${AuthEndPoints.MARK_USER_NOTIFICATION_READ}/${notification.id}/read`, ApiMethod.PATCH, {}, false, { suppressToast: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          notification.is_read = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
          this.notificationRefreshSvc.notify();
          this.cdr.markForCheck();
          openRedirect();
        },
      });
  }

  protected trackNotification(_: number, notification: UserNotification): number {
    return notification.id;
  }

  protected getNotificationSerial(index: number): number {
    return (this.pagination.page_no - 1) * this.pagination.page_size + index + 1;
  }

  protected getNotificationDate(notification: UserNotification): string {
    if (!this.preferencesSvc.showBsDate || !notification.created_at_bs) {
      return notification.created_at_ad ?? '-';
    }

    if (/\d{2}:\d{2}(?::\d{2})?$/.test(notification.created_at_bs)) {
      return notification.created_at_bs;
    }

    const time = /\d{2}:\d{2}(?::\d{2})?$/.exec(notification.created_at_ad ?? '')?.[0];
    return time ? `${notification.created_at_bs} ${time}` : notification.created_at_bs;
  }

  protected canShowPagination(): boolean {
    return canShowPagination(this.pagination.total_records);
  }

  protected getPaginationLabel(): string {
    return buildPaginationLabel(this.pagination, 'No records', 'Showing', 'results');
  }

  protected getPaginationItems(): number[] {
    return buildPaginationItems(this.pagination.total_pages, this.pagination.page_no);
  }

  protected previousPage(): void {
    this.goToPage(this.pagination.page_no - 1);
  }

  protected nextPage(): void {
    this.goToPage(this.pagination.page_no + 1);
  }

  protected goToPage(pageNo: number): void {
    if (pageNo === this.pagination.page_no || pageNo < 1 || pageNo > this.pagination.total_pages) {
      return;
    }
    this.loadNotifications(pageNo);
  }

  private loadNotifications(pageNo = this.pagination.page_no): void {
    const requestId = ++this.notificationRequestId;
    this.isLoading = true;
    const query = buildQueryString({
      page_no: pageNo,
      page_size: this.pageSizeControl.value,
    });
    this.httpSvc
      .requestCall(AuthEndPoints.GET_ALL_USER_NOTIFICATIONS, ApiMethod.GET, query, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (requestId !== this.notificationRequestId) {
            return;
          }
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          if (requestId !== this.notificationRequestId) {
            return;
          }
          const value = response?.value ?? {};
          this.notifications = Array.isArray(value.notifications) ? value.notifications : [];
          this.unreadCount = Number(value.unread_count ?? 0) || 0;
          this.pagination = extractPagination<NotificationPagination>(value) ?? this.pagination;
        },
        error: () => {
          if (requestId !== this.notificationRequestId) {
            return;
          }
          this.notifications = [];
          this.pagination = {
            page_no: pageNo,
            page_size: this.pageSizeControl.value,
            total_records: 0,
            total_pages: 0,
          };
        },
      });
  }
}
